# Phase 2: Job Posting

With the consolidation of the SEEK, Jobstreet and Jobsdb platforms, we have changed the way job ads are surfaced across our markets.
Your job posting integration must be revised to ensure that it correctly interoperates with the SEEK API going forward.

Job ads posted via the SEEK API are syndicated across our candidate websites,
including [au.seek.com], [nz.seek.com], [jobstreet.com] and [jobsdb.com].
Each website defaults to a market-specific location filter but candidates are able to manually search for international job ads.

When your software retrieves the [`positionUri` field] in response to a [`PositionProfilePosted` event],
it will receive a localised link to the live job ad.
This is based on the hirer’s country as configured in SEEK;
a hirer based in Singapore is assigned a [sg.jobstreet.com] link,
while a hirer based in Australia continues to be assigned a [au.seek.com] link.

[`PositionProfilePosted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`positionUri` field]: /schema/named-type/PositionProfile#/field/positionUri
[jobsdb.com]: https://www.jobsdb.com/
[jobstreet.com]: https://www.jobstreet.com/
[nz.seek.com]: https://nz.seek.com/
[sg.jobstreet.com]: https://sg.jobstreet.com/
[au.seek.com]: https://au.seek.com/

## Step 1: Understanding your scope

We have extended the following job posting features to satisfy regional requirements across the Asia Pacific.
The revision is required even if you do not offer your recruitment software in these additional markets,
as it includes changes to marketplace features that affect Australia & New Zealand hirers.

import { JobPostingFeatureList } from './job-posting-feature-list';

<JobPostingFeatureList />

## Step 2: Start building

import { Alert, List, Stack, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

<Alert tone="info">
  <Stack space="medium">
    <Text>Before you commence your integration, ensure that:</Text>
    <List>
      <Text>
        Your user experience is in line with our{' '}
        <SmartTextLink href="/use-cases/job-posting/user-experience-guide">
          job posting user experience guide
        </SmartTextLink>
        , and if implementing ad selection via GraphQL, adheres to our{' '}
        <SmartTextLink href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection/graphql/user-experience-guide">
          ad selection user experience guide
        </SmartTextLink>
        .
      </Text>
      <Text>
        You have access to the SEEK test hirer provided for development and
        testing purposes. Test hirers can post job ads free of charge, but their
        job ads don’t appear in SEEK’s search results.
      </Text>
    </List>
    <Text>
      If you require any technical support during your integration, contact our{' '}
      <SmartTextLink href="https://forms.office.com/r/SCZdDyx0BJ">
        Integration Service Desk
      </SmartTextLink>
      , available Monday to Friday 9:00am—5:00pm AET.
    </Text>
  </Stack>
</Alert>

The checklist below outlines best practices for integrating each job posting feature:

import { JumpTo } from './JumpTo';

### Locations

_If you have already built a supported [implementation option](/use-cases/job-posting/locations#implementation-options) in your software you may skip this step._

If your software maintains a static mapping from its internal location hierarchy to SEEK location IDs:

- Review available options within our [Location documentation](/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/locations)
- Implement API option most appropriate to your software
- Remove static mapping

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/locations"
  label="Locations"
/>

### Job categories

_This change ensures that hirers are only presented with relevant categories and that those categories are matched to the region the job is being posted in._

- Call the jobCategories query dynamically for each job ad, passing the current state of the ad in the `positionProfile` field
- Ensure that all `categories / sub-categories` returned are visible and sub-categories are selectable by the hirer

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/job-categories"
  label="Job categories"
/>

### Salary

- Remove deprecated `CommissionOnly` pay type
- Remove any hard coded validation values (such as limiting max salary to a fixed value)
- Implement error display for salary values as defined in our [Salary validation documentation](/use-cases/job-posting/salary#validation)
- Implement [recommended UX updates for Salary Range](/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/salary#salary-range)

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/salary"
  label="Salary"
/>

### Ad selection

_SEEK strongly recommends using the new Ad Selection Panel for your integration. It will significantly reduce development time and ongoing maintenance, as well as provide an up-to-date user experience for SEEK hirers._

Ad Selection now requires the use of `seekAdvertisementProductId`. Using the deprecated `seekAnzAdvertisementType` will result in failed Job Posting create and update calls following the December 2024 retirement date.

- Review available options within our Ad Selection documentation
- If migrating from the previous Ad Selection Panel:
  - Update script tag and render function with revised props
  - Call render function on page load and when changes to `positionProfile` input are made
  - Replace any hard coded logic for brand selection / bullet point inputs with conditional logic based on features returned in `onChange`
  - Replace references to `seekAnzAdvertisementType` with `seekAdvertisementProductId` field when previewing, posting or updating the job ad
- If using GraphQL integration:
  - Review the [user experience guide][ad-selection-graphql-ux] and ensure that your implementation will meet our new requirements
  - Rebuild calls to use the new `advertisementProducts` query
  - Call query on page load and whenever the positionProfile input changes
  - Adjust UI to handle a variable number of products (we recommend accommodating 4) or provide a `limit` param
  - Ensure your UI pre-selects the ad product marked with `selected` if one exists (typically returned when updating a job ad or when using `selectedAdvertisementProductId` for rehydrating a draft)
  - Remove any hardcoded logic based on specific names or identifiers
  - Replace references to `seekAnzAdvertisementType` with `seekAdvertisementProductId` field when previewing, posting or updating the job ad

[ad-selection-graphql-ux]: /migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection/graphql/user-experience-guide

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection"
  label="Ad selection"
/>

### Branding

- Display selectable branding elements to hirer retrieved from Brandings query
- Pass selected branding ID as part of `positionProfile` in preview, create and update calls
- Ensure correct branding is displayed as selected during ad edit
- Display branding selection based on ad product features

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/branding"
  label="Branding"
/>

### Questionnaires

_Note that the SEEK API does not allow questionnaires to be modified once a job ad is posted. See the [questionnaire immutability](/use-cases/job-posting/questionnaires#immutability) documentation for more information._

- Review available options within our Questionnaire documentation
- If you have previously implemented the SEEK Questionnaire Panel:
  - Update script tag and render function call
  - Remove any references to `errorHandler`
  - Update calls from `SEEKQuestionnairePanel.save() to dispatchEvent('seek:questionnaire:save');`
  - Update handling of errorCode / isError
- If you’re implementing the SEEK Questionnaire Panel for the first time:
  - Add script tag to the page
  - Call render function on page load and when changes to `positionProfile` input are made
  - Handle browser token request
  - Call `dispatchEvent('seek:questionnaire:save')` prior to previewing, posting or saving a draft of the job ad
- If you have an existing question library and are implementing our ATS Screening Questions functionality:
  - Implement the `createApplicationQuestionnaire` mutation
  - Post job ad with returned questionnaire ID

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/questionnaires"
  label="Questionnaires"
/>

### Job ad preview

_If you have already built job ad preview then skip this step. However, you should review two other changes to the `postedPositionProfilePreview` query for Ad Selection and Questionnaire._

- Display a button for hirers to click on to preview the job ad
- Execute the `postedPositionProfilePreview` query to generate a job ad preview when the button is clicked
- Ensure the full state of the ad is passed to the positionProfile argument
- Open new tab or render iframe in the flow to the returned `previewUri` field

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/job-ad-preview"
  label="Job ad preview"
/>

### Access to Asia features

If you have hirers in Asia or hirers in ANZ wishing to post ads in Asia, you can enable job posting in our six Asia markets by supporting localised salaries and content.

#### Salary

- Implement the `Month`ly pay interval
- Display a shortlist of currencies for hirers to select
  - _We recommend retrieving the shortlist of currencies from our currencies query to support further updates._
- Pre-select the recommended currency based on job location

#### Content localisation

- Send an appropriate `Accept-Language` header in GraphQL requests

<JumpTo
  href="/migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/content-localisation"
  label="Content localisation"
/>

## Step 3: Test your integration

Before you apply for SEEK Certification, thoroughly test your integration for the successful execution of the following acceptance criteria.

- All other features and capabilities previously supported are expected to continue to work as-is and will not be negatively impacted by the Job Posting revision changes.
- Hardcoding or storing of data for ad product features, locations or associated logic is no longer supported.
  Dynamic SEEK API calls MUST be used for retrieving all data to ensure all information is current and mitigate the risk of breaking changes rolled out by SEEK in the future.

import { JobPostingAcceptanceCriteria } from './job-posting-acceptance-criteria';

<JobPostingAcceptanceCriteria />

## Step 4: Certification

Before proceeding, make sure you have:

1. Built the mandatory features detailed in [Step 1] and [Step 2]
2. Tested that the features work correctly based on the acceptance criteria in [Step 3]

If anything is missing from your current implementation, do not proceed further until you have implemented all mandatory features.

[Step 1]: #step-1-understanding-your-scope
[Step 2]: #step-2-start-building
[Step 3]: #step-3-test-your-integration

### Applying for certification

1. Plan to go through a step-by-step process of drafting, posting, updating and expiring a job ad.
   If applicable for your software, also include job scheduling and automatic re-posting processes.
2. Record 2 to 3 videos that demonstrate the full user experience of the Job Posting flow indicated in the downloadable Test Steps Excel Workbook.
3. Submit the video recordings and links to test jobs with your certification request to the [Integration Service Desk].

import { TestCasesFile } from './assets/TestCasesFile';

<TestCasesFile />

Note the following:

- During your testing, enter the **exact test data** indicated and fully demonstrate the feature exactly as described in each step
- Certification will only be granted by SEEK if we receive complete coverage of the test cases in your video submissions
- SEEK will notify you if it identifies any issues with your integration
- All issues must be resolved to obtain successful certification and prior to live deployment
- For further review after making corrections, resubmit a new video to the [Integration Service Desk]

[Integration Service Desk]: https://forms.office.com/r/SCZdDyx0BJ

#### Certification request

When submitting your certification request, include:

- Your anticipated integration release date
- The date you began recording the test case videos
- Links to the test case videos
- URL for the test job ad posted to `au.seek.com` (this will appear as expired after completion of the test cases)
- URL for the test job ad posted to `my.jobstreet.com` (this will appear as expired after completion of the test cases)

#### Video requirements

- Each video should not exceed 10 minutes
- The video file must be downloadable
- The video file format should be MP4 video format
- The file name must follow this format: `Partner Name_JP_CertGroup.mp4`

## Step 5: Release

Once your build has been certified, you are ready to release subject to completing the below steps.

Allow a 1 week lead time to allow for these steps to be completed.

### 1. Confirm your release plan

- Advise SEEK of your planned release date.
- Advise SEEK of your customer release communications approach including when they are sent to hirers.

### 2. Submit release communications for approval

- SEEK’s Release Pack includes release messaging and branding assets, which will be made available to you once your build has started.
  Using the Release Pack you can easily develop content that showcases the new value you have created for our mutual hirers.
- Before you share your content with hirers, provide it to SEEK by emailing [APIImplementation@seek.com.au](mailto:APIImplementation@seek.com.au) so it can be approved.
- SEEK will review your messaging within 2 business days.
  Once approved, you are ready to publish it!

### 3. Confirm your release

- On the day of release, confirm with us that the release has been successfully completed.
